import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Settings, FileUp, Receipt, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ManageProcurementSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSyncReservations: () => void;
    onSyncTransactions: () => void;
}

interface ActionCardProps {
    icon: any;
    title: string;
    description: string;
    onClick: () => void;
    colorClass: string;
}

const ActionCard = ({ icon: Icon, title, description, onClick, colorClass }: ActionCardProps) => (
    <button
        onClick={onClick}
        className="w-full text-left group relative transition-all duration-300 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 hover:scale-[1.01]"
    >
        <Card className="border border-border shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:border-primary/20">
            <div className={cn(
                "absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500",
                colorClass.replace('text-', 'bg-')
            )} />

            <CardContent className="p-3 flex items-center gap-3">
                <div className={cn(
                    "p-2 rounded-lg transition-all duration-300 shadow-sm shrink-0",
                    "bg-white dark:bg-muted border border-muted",
                    "group-hover:scale-105",
                    colorClass
                )}>
                    <Icon className="h-5 w-5" />
                </div>

                <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground tracking-tight">{title}</h3>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                    <p className="text-sm text-muted-foreground leading-snug">{description}</p>
                </div>
            </CardContent>
        </Card>
    </button>
);

export default function ManageProcurementSheet({
    open,
    onOpenChange,
    onSyncReservations,
    onSyncTransactions,
}: ManageProcurementSheetProps) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[90vw] sm:!w-[500px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6 flex flex-col gap-6"
            >
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-primary font-medium tracking-wide text-xs uppercase">
                        <Settings className="h-3.5 w-3.5" />
                        Procurement
                    </div>
                    <SheetTitle className="text-2xl font-bold">Manage</SheetTitle>
                    <p className="text-muted-foreground text-sm">
                        Select an action to perform.
                    </p>
                </div>

                <div className="grid gap-2 overflow-y-auto p-1 -m-1 content-start">
                    <ActionCard
                        icon={FileUp}
                        title="Sync Reservations"
                        description="Import reserved & purchased vehicles from SBT PDF."
                        onClick={() => {
                            onOpenChange(false);
                            onSyncReservations();
                        }}
                        colorClass="text-blue-600"
                    />

                    <ActionCard
                        icon={Receipt}
                        title="Sync Transactions"
                        description="Import bank deposits and match to vehicles."
                        onClick={() => {
                            onOpenChange(false);
                            onSyncTransactions();
                        }}
                        colorClass="text-emerald-600"
                    />
                </div>
            </SheetContent>
        </Sheet>
    );
}
